import React, { useState, useEffect, useCallback, useMemo, useRef, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as XLSX from "xlsx";
import { API_BASE } from "../utils/api";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import GroupsIcon from "@mui/icons-material/Groups";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CloseIcon from "@mui/icons-material/Close";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import AddchartIcon from "@mui/icons-material/Addchart";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

/* ───────────────────────────────────────────────────────────────────────
   Vyapar Sales — full dealer workspace inside SOTA. Dashboard + My Customers
   (add) + Customers Sales (add sale). Data is stored in the SHARED influencer-
   scheme "Dealers" collection (dealer.users[]), so it also appears in the
   influencer app.
──────────────────────────────────────────────────────────────────────────*/

const CUSTOMER_TYPES = [
  "Contractor",
  "Kadiya (Mason)",
  "Engineer / Consultant",
  "Builder",
  "Fabricator",
  "End User (Individual House)",
];

function getDealerPhone() {
  const direct = String(localStorage.getItem("loginPhone") || "").replace(/\D/g, "");
  if (direct.length >= 10) return direct.slice(-10);
  try {
    const d = JSON.parse(localStorage.getItem("dealer") || "{}");
    const p = String(d.phone || d["Contact No"] || "").replace(/\D/g, "");
    if (p.length >= 10) return p.slice(-10);
  } catch {}
  return "";
}

function getDealerProfile() {
  try {
    const d = JSON.parse(localStorage.getItem("dealer") || "{}");
    return {
      partyName: d.firmName || d["Company Name"] || d.Particulars || d.name || "",
      gst: d.gst || d["GSTIN/UIN"] || "",
      contactPerson: d.name || d["Contact Person Name"] || "",
      district: d.district || d.DIST || d.Area || "",
      area: d.area || d.Area || "",
      address: d.address || d.Address || "",
    };
  } catch {
    return {};
  }
}

const EMPTY_CUST = { name: "", mobile: "", type: "", district: "", area: "", address: "", profilePic: "", aadhar: "" };

// Bulk-upload fields + the loose header names we auto-match against.
const FIELD_DEFS = [
  { key: "name", label: "Customer Name", required: true, eg: "Ramesh Patel",
    names: ["name", "customer name", "customer", "party name", "partyname"] },
  { key: "mobile", label: "Mobile", required: true, eg: "9876543210",
    names: ["mobile", "mobile number", "mobile no", "phone", "contact", "contact no", "number", "whatsapp"] },
  { key: "type", label: "Type", required: false, eg: "Mason",
    names: ["type", "customer type", "category"] },
  { key: "area", label: "Area / Locality", required: false, eg: "Vastral",
    names: ["area", "area / locality", "locality"] },
  { key: "district", label: "District", required: false, eg: "Ahmedabad",
    names: ["district", "dist"] },
  { key: "address", label: "Full Address", required: false, eg: "Shop 4, Main Road",
    names: ["address", "full address", "addr"] },
];

// Auto-match each field to a column header from the file.
function autoMapColumns(columns) {
  const map = {};
  const lc = columns.map((c) => ({ raw: c, low: String(c).trim().toLowerCase() }));
  FIELD_DEFS.forEach((f) => {
    const hit = lc.find((c) => f.names.includes(c.low)) ||
      lc.find((c) => f.names.some((n) => c.low.includes(n)));
    map[f.key] = hit ? hit.raw : "";
  });
  return map;
}

const todayStr = () => {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
};

const fmtDate = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return "";
  const dd = String(dt.getDate()).padStart(2, "0");
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${dt.getFullYear()}`;
};

export default function VyaparSales() {
  const navigate = useNavigate();
  const phone = getDealerPhone();

  const [view, setView] = useState("dashboard"); // dashboard | customers | sales
  const [dashTab, setDashTab] = useState("customers"); // dashboard lower section: customers | rewards
  const [custPage, setCustPage] = useState(1); // Customers table pagination
  const PAGE_SIZE = 10;
  const [customers, setCustomers] = useState([]);

  // AI scan — dealer ek photo (visiting card / customer list) upload kare,
  // Gemini usme se customer details nikaal ke auto-add kar de.
  const [aiBusy, setAiBusy] = useState(false);
  const aiFileRef = useRef(null);
  const [aiRows, setAiRows] = useState([]); // AI-extracted rows awaiting review
  const [showAiPreview, setShowAiPreview] = useState(false);
  const [aiSaving, setAiSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  // dealer rewards / target (admin-set; only for registered dealers)
  const [rewards, setRewards] = useState([]);
  const [registered, setRegistered] = useState(false);
  const [target, setTarget] = useState(null); // admin-set target { quantity, startDate, endDate }
  const [rankInfo, setRankInfo] = useState(null); // { myTotalKg, rank, totalDealers, topTotalKg }

  // customer modal (add OR edit — editCustId set => edit)
  const [showCust, setShowCust] = useState(false);
  const [cust, setCust] = useState(EMPTY_CUST);
  const [editCustId, setEditCustId] = useState(null);
  const [savingCust, setSavingCust] = useState(false);

  // sale modal (add OR edit — editSaleId set => edit)
  const [showSale, setShowSale] = useState(false);
  const [saleCustomerId, setSaleCustomerId] = useState("");
  const [editSaleId, setEditSaleId] = useState(null);
  const [sale, setSale] = useState({ date: todayStr(), quantity: "", remarks: "" });
  const [savingSale, setSavingSale] = useState(false);

  // expanded customer in sales view
  const [expanded, setExpanded] = useState("");

  // customer detail modal (photo + aadhaar + sales)
  const [detailCust, setDetailCust] = useState(null);
  const [lightboxImg, setLightboxImg] = useState(""); // full-screen image preview

  // bulk upload
  const [showBulk, setShowBulk] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [sheetUrl, setSheetUrl] = useState("");
  const [bulkResult, setBulkResult] = useState(null);
  const [previewRows, setPreviewRows] = useState([]); // parsed rows awaiting Save
  const [previewName, setPreviewName] = useState(""); // source file name
  const [fileColumns, setFileColumns] = useState([]); // header names from file
  const [colMap, setColMap] = useState({}); // field key -> file column name

  const setC = (k, v) => setCust((f) => ({ ...f, [k]: v }));
  const setS = (k, v) => setSale((f) => ({ ...f, [k]: v }));

  // Resize + compress an image file → small base64 JPEG (keeps payload light).
  const compressImage = (file, maxDim = 1000, quality = 0.7) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = reject;
      reader.onload = () => {
        const img = new Image();
        img.onerror = reject;
        img.onload = () => {
          let { width: w, height: h } = img;
          if (w > h && w > maxDim) {
            h = Math.round((h * maxDim) / w);
            w = maxDim;
          } else if (h >= w && h > maxDim) {
            w = Math.round((w * maxDim) / h);
            h = maxDim;
          }
          const canvas = document.createElement("canvas");
          canvas.width = w;
          canvas.height = h;
          canvas.getContext("2d").drawImage(img, 0, 0, w, h);
          resolve(canvas.toDataURL("image/jpeg", quality));
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    });

  // Handle a customer photo / aadhaar file pick → compress → store in cust state.
  const pickCustPhoto = async (key, file) => {
    if (!file) return;
    try {
      const b64 = await compressImage(file, key === "aadhar" ? 1400 : 900, 0.7);
      setC(key, b64);
    } catch {
      toast.error("Photo read nahi hui, dobara try karo.");
    }
  };

  const load = useCallback(async () => {
    if (!phone) {
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(
        `${API_BASE}/api/customers?phone=${encodeURIComponent(phone)}`,
        { headers: { "x-auth-phone": phone } }
      );
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setCustomers(Array.isArray(data.customers) ? data.customers : []);
        setRewards(Array.isArray(data.rewards) ? data.rewards : []);
        setRegistered(!!data.registered);
        setTarget(data.target || null);
      } else if (res.status === 503) {
        toast.error("Vyapar Sales backend not configured yet.");
      }
    } catch {
      /* keep */
    } finally {
      setLoading(false);
    }
  }, [phone]);

  useEffect(() => {
    load();
  }, [load]);


  // ── derived stats ──
  const stats = useMemo(() => {
    let totalSales = 0;
    let entries = 0;
    customers.forEach((c) => {
      (c.sales || []).forEach((s) => {
        totalSales += Number(s.quantity) || 0;
        entries += 1;
      });
    });
    return { totalCustomers: customers.length, totalSales, entries };
  }, [customers]);

  // ── Target vs Achievement (+ reward-tier "rank") ──
  const achievement = useMemo(() => {
    const targetKg = Number(target?.quantity || 0);
    const achieved = stats.totalSales;
    const pct = targetKg > 0 ? Math.min(100, (achieved / targetKg) * 100) : 0;
    const remaining = Math.max(0, targetKg - achieved);
    const tiers = [...rewards].sort((a, b) => (a.fromKg || 0) - (b.fromKg || 0));
    // current tier = highest tier whose fromKg <= achieved
    let currentIdx = -1;
    tiers.forEach((t, i) => {
      if (achieved >= Number(t.fromKg || 0)) currentIdx = i;
    });
    const currentTier = currentIdx >= 0 ? tiers[currentIdx] : null;
    const nextTier = tiers[currentIdx + 1] || null;
    const toNext = nextTier ? Math.max(0, Number(nextTier.fromKg || 0) - achieved) : 0;
    return {
      targetKg,
      achieved,
      pct,
      remaining,
      tiers,
      currentTier,
      currentRank: currentIdx + 1, // 1-based tier rank (0 = none reached)
      totalTiers: tiers.length,
      nextTier,
      toNext,
    };
  }, [target, rewards, stats.totalSales]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter(
      (c) =>
        String(c.name || "").toLowerCase().includes(q) ||
        String(c.mobile || "").includes(q) ||
        String(c.type || "").toLowerCase().includes(q)
    );
  }, [customers, search]);

  // Reward a customer has earned based on THEIR OWN total sales (kg).
  const rewardForKg = (kg) => {
    const tiers = [...rewards].sort((a, b) => (a.fromKg || 0) - (b.fromKg || 0));
    const current = [...tiers].reverse().find((t) => kg >= Number(t.fromKg || 0));
    const next = tiers.find((t) => Number(t.fromKg || 0) > kg);
    return {
      current: current ? (current.rewards || []).filter(Boolean).join(", ") : "",
      next: next ? (next.rewards || []).filter(Boolean).join(", ") : "",
      gap: next ? Math.max(0, Number(next.fromKg) - kg) : 0,
    };
  };

  // ── Customers table pagination ──
  const custTotalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageSafe = Math.min(custPage, custTotalPages);
  const pagedCustomers = filtered.slice(
    (pageSafe - 1) * PAGE_SIZE,
    pageSafe * PAGE_SIZE
  );
  useEffect(() => {
    setCustPage(1);
  }, [search, view]);

  // ── open customer modal (add or edit) ──
  const openAddCustomer = () => {
    setEditCustId(null);
    setCust(EMPTY_CUST);
    setShowCust(true);
  };
  const openEditCustomer = (c) => {
    setEditCustId(String(c._id));
    setCust({
      name: c.name || "",
      mobile: c.mobile || "",
      type: c.type || "",
      district: c.district || "",
      area: c.area || "",
      address: c.address || "",
      profilePic: c.profilePic || "",
      aadhar: c.documents?.aadhar || "",
    });
    setShowCust(true);
  };

  // ── add / edit customer ──
  const submitCustomer = async (e) => {
    e?.preventDefault?.();
    if (savingCust) return;
    const name = cust.name.trim();
    const mobile = cust.mobile.replace(/\D/g, "");
    if (!name) return toast.error("Customer name is required.");
    if (/[0-9]/.test(name)) return toast.error("Name should not contain numbers.");
    if (!/^[6-9]\d{9}$/.test(mobile))
      return toast.error("Enter a valid 10-digit mobile number.");
    if (!cust.type) return toast.error("Please select a customer type.");

    // Client-side duplicate-mobile guard (backend enforces too).
    const dup = customers.some(
      (c) =>
        String(c._id) !== String(editCustId || "") &&
        String(c.mobile).replace(/\D/g, "") === mobile
    );
    if (dup) return toast.error("This mobile number is already added.");

    setSavingCust(true);
    try {
      const isEdit = !!editCustId;
      const url = isEdit
        ? `${API_BASE}/api/customers/${editCustId}`
        : `${API_BASE}/api/customers/add`;
      const res = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: { "Content-Type": "application/json", "x-auth-phone": phone },
        body: JSON.stringify({
          dealerPhone: phone,
          name,
          mobile,
          type: cust.type,
          district: cust.district.trim(),
          area: cust.area.trim(),
          address: cust.address.trim(),
          profilePic: cust.profilePic || "",
          aadhar: cust.aadhar || "",
          dealerProfile: getDealerProfile(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        toast.success(isEdit ? "Customer updated ✓" : "Customer added ✓");
        setCust(EMPTY_CUST);
        setShowCust(false);
        setEditCustId(null);
        if (isEdit) {
          setCustomers((prev) =>
            prev.map((c) =>
              String(c._id) === String(editCustId)
                ? { ...c, ...data.customer, sales: c.sales || [] }
                : c
            )
          );
        } else {
          setCustomers((prev) => [
            { ...data.customer, sales: data.customer.sales || [] },
            ...prev,
          ]);
        }
      } else {
        toast.error(data.message || "Could not save customer.");
      }
    } catch {
      toast.error("Network error. Please try again.");
    } finally {
      setSavingCust(false);
    }
  };

  // ── AI: photo se customer(s) nikaal ke add karo ──
  // Gemini is called through our backend proxy — the API key stays server-side
  // (env), never in this bundle. A hardcoded key here got auto-disabled by
  // Google once it shipped to browsers.

  // file -> downscaled base64 (no data: prefix). Downscaling matters: the
  // vision model's free tier allows only ~8000 tokens/min, and a full-size
  // phone photo can exceed that in a single call (→ 429). ~1000px keeps it
  // well under budget while staying readable.
  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          try {
            const maxSide = 1000;
            const ratio = Math.min(maxSide / img.width, maxSide / img.height, 1);
            const w = Math.round(img.width * ratio);
            const h = Math.round(img.height * ratio);
            const canvas = document.createElement("canvas");
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext("2d");
            ctx.fillStyle = "#ffffff";
            ctx.fillRect(0, 0, w, h);
            ctx.drawImage(img, 0, 0, w, h);
            resolve(canvas.toDataURL("image/jpeg", 0.65).split(",")[1] || "");
          } catch (err) {
            reject(err);
          }
        };
        img.onerror = reject;
        img.src = String(reader.result || "");
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  // add one customer via API; returns "added" | "dup" | "error"
  const addCustomerDirect = async (c, existingMobiles) => {
    const name = String(c.name || "").trim();
    const mobile = String(c.mobile || "").replace(/\D/g, "").slice(-10);
    if (!name || !/^[6-9]\d{9}$/.test(mobile)) return "error";
    if (existingMobiles.has(mobile)) return "dup";
    // map type loosely to one of our allowed types
    const rawType = String(c.type || "").trim().toLowerCase();
    const type =
      CUSTOMER_TYPES.find((t) => t.toLowerCase() === rawType) ||
      CUSTOMER_TYPES.find((t) => rawType && t.toLowerCase().includes(rawType)) ||
      "";
    try {
      const res = await fetch(`${API_BASE}/api/customers/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-auth-phone": phone },
        body: JSON.stringify({
          dealerPhone: phone,
          name,
          mobile,
          type,
          district: String(c.district || "").trim(),
          area: String(c.area || "").trim(),
          address: String(c.address || "").trim(),
          dealerProfile: getDealerProfile(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        existingMobiles.add(mobile);
        setCustomers((prev) => [
          { ...data.customer, sales: data.customer.sales || [] },
          ...prev,
        ]);
        return "added";
      }
      return /already/i.test(data.message || "") ? "dup" : "error";
    } catch {
      return "error";
    }
  };

  const handleAiPhoto = async (file) => {
    if (!file) return;
    if (!phone) return toast.error("Login number detect nahi hua.");
    setAiBusy(true);
    const tId = toast.loading("AI photo padh raha hai…");
    try {
      const base64 = await fileToBase64(file);
      const prompt =
        "You are reading an image that may contain one OR MANY customer/contact " +
        "entries (a visiting card, a handwritten list, a phone contact, a register page). " +
        "Extract every distinct person. Return ONLY a JSON array, no markdown. " +
        'Each item: {"name": string, "mobile": string (10 digit Indian, digits only), ' +
        '"type": string (one of: Contractor, Kadiya (Mason), Engineer / Consultant, Builder, Fabricator, End User (Individual House); else ""), ' +
        '"area": string, "district": string, "address": string}. ' +
        "If a field is unknown use empty string. Skip entries without a valid 10-digit mobile.";
      const resp = await fetch(`${API_BASE}/api/ai/gemini`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contents: [
            {
              role: "user",
              parts: [
                { text: prompt },
                { inline_data: { mime_type: "image/jpeg", data: base64 } },
              ],
            },
          ],
          generationConfig: {
            temperature: 0,
            topK: 1,
            topP: 0.8,
            maxOutputTokens: 1024,
            thinkingConfig: { thinkingBudget: 0 },
          },
        }),
      });
      if (resp.status === 429) {
        toast.update(tId, {
          render: "AI abhi busy hai (limit). Thodi der baad dobara try karein.",
          type: "warning",
          isLoading: false,
          autoClose: 4000,
        });
        return;
      }
      if (!resp.ok) throw new Error(`AI ${resp.status}`);
      const data = await resp.json();
      const raw =
        data?.candidates?.[0]?.content?.parts?.map((p) => p?.text || "").join("\n") || "";
      const cleaned = raw.replace(/```json/gi, "").replace(/```/g, "").trim();
      const s = cleaned.indexOf("[");
      const e = cleaned.lastIndexOf("]");
      let list = [];
      if (s !== -1 && e !== -1) {
        try {
          list = JSON.parse(cleaned.slice(s, e + 1));
        } catch {
          list = [];
        }
      }
      if (!Array.isArray(list) || list.length === 0) {
        toast.update(tId, {
          render: "Photo me koi valid customer (10-digit mobile ke saath) nahi mila.",
          type: "warning",
          isLoading: false,
          autoClose: 4000,
        });
        return;
      }
      // Normalize into editable rows (don't add yet — show preview first).
      const rows = list.map((c) => {
        const rawType = String(c.type || "").trim().toLowerCase();
        const type =
          CUSTOMER_TYPES.find((t) => t.toLowerCase() === rawType) ||
          CUSTOMER_TYPES.find((t) => rawType && t.toLowerCase().includes(rawType)) ||
          "";
        return {
          name: String(c.name || "").trim(),
          mobile: String(c.mobile || "").replace(/\D/g, "").slice(-10),
          type,
          area: String(c.area || "").trim(),
          district: String(c.district || "").trim(),
          address: String(c.address || "").trim(),
        };
      });
      setAiRows(rows);
      setShowAiPreview(true);
      toast.update(tId, {
        render: `AI: ${rows.length} customer(s) mile — review karke Save karo.`,
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });
    } catch (err) {
      toast.update(tId, {
        render: "AI scan fail hua. Dobara try karo.",
        type: "error",
        isLoading: false,
        autoClose: 4000,
      });
    } finally {
      setAiBusy(false);
      if (aiFileRef.current) aiFileRef.current.value = "";
    }
  };

  // edit one AI preview row field / remove a row
  const setAiRow = (i, k, v) =>
    setAiRows((rows) => rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const removeAiRow = (i) => setAiRows((rows) => rows.filter((_, idx) => idx !== i));

  // Save all reviewed AI rows as customers
  const saveAiRows = async () => {
    if (aiSaving) return;
    const valid = aiRows.filter(
      (r) => r.name.trim() && /^[6-9]\d{9}$/.test(String(r.mobile).replace(/\D/g, "").slice(-10))
    );
    if (valid.length === 0)
      return toast.error("Har row me valid naam aur 10-digit mobile chahiye.");
    setAiSaving(true);
    const existingMobiles = new Set(
      customers.map((c) => String(c.mobile || "").replace(/\D/g, "").slice(-10))
    );
    let added = 0,
      dup = 0,
      err = 0;
    for (const r of valid) {
      const res = await addCustomerDirect(r, existingMobiles);
      if (res === "added") added++;
      else if (res === "dup") dup++;
      else err++;
    }
    setAiSaving(false);
    const parts = [`${added} added`];
    if (dup) parts.push(`${dup} duplicate`);
    if (err) parts.push(`${err} skipped`);
    toast.success(`AI: ${parts.join(", ")} ✓`);
    setShowAiPreview(false);
    setAiRows([]);
  };

  // ── open sale modal (add or edit) ──
  const openSale = (customerId = "") => {
    setEditSaleId(null);
    setSaleCustomerId(customerId);
    setSale({ date: todayStr(), quantity: "", remarks: "" });
    setShowSale(true);
  };
  const openEditSale = (customerId, s) => {
    setEditSaleId(String(s._id));
    setSaleCustomerId(String(customerId));
    setSale({
      date: s.date ? new Date(s.date).toISOString().slice(0, 10) : todayStr(),
      quantity: String(s.quantity ?? ""),
      remarks: s.remarks || "",
    });
    setShowSale(true);
  };

  // ── add / edit sale ──
  const submitSale = async (e) => {
    e?.preventDefault?.();
    if (savingSale) return;
    if (!saleCustomerId) return toast.error("Please select a customer.");
    const qty = Number(sale.quantity);
    if (!sale.date) return toast.error("Please select a date.");
    if (!Number.isFinite(qty) || qty <= 0) return toast.error("Enter a valid quantity (KG).");

    setSavingSale(true);
    try {
      const isEdit = !!editSaleId;
      const url = isEdit
        ? `${API_BASE}/api/customers/${saleCustomerId}/sales/${editSaleId}`
        : `${API_BASE}/api/customers/${saleCustomerId}/sales`;
      const res = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: { "Content-Type": "application/json", "x-auth-phone": phone },
        body: JSON.stringify({ date: sale.date, quantity: qty, remarks: sale.remarks }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        toast.success(isEdit ? "Sale updated ✓" : "Sale added ✓");
        setShowSale(false);
        setEditSaleId(null);
        setCustomers((prev) =>
          prev.map((c) => {
            if (String(c._id) !== String(saleCustomerId)) return c;
            const sales = c.sales || [];
            return {
              ...c,
              sales: isEdit
                ? sales.map((s) => (String(s._id) === String(editSaleId) ? data.sale : s))
                : [...sales, data.sale],
            };
          })
        );
      } else {
        toast.error(data.message || "Could not save sale.");
      }
    } catch {
      toast.error("Network error. Please try again.");
    } finally {
      setSavingSale(false);
    }
  };

  const deleteCustomer = async (id) => {
    if (!window.confirm("Remove this customer?")) return;
    try {
      const res = await fetch(`${API_BASE}/api/customers/${id}`, {
        method: "DELETE",
        headers: { "x-auth-phone": phone },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setCustomers((prev) => prev.filter((c) => String(c._id) !== String(id)));
        toast.success("Customer removed");
      } else toast.error(data.message || "Could not remove.");
    } catch {
      toast.error("Network error.");
    }
  };

  const deleteSale = async (customerId, saleId) => {
    if (!window.confirm("Delete this sale entry?")) return;
    try {
      const res = await fetch(`${API_BASE}/api/customers/${customerId}/sales/${saleId}`, {
        method: "DELETE",
        headers: { "x-auth-phone": phone },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setCustomers((prev) =>
          prev.map((c) =>
            String(c._id) === String(customerId)
              ? { ...c, sales: (c.sales || []).filter((s) => String(s._id) !== String(saleId)) }
              : c
          )
        );
        toast.success("Sale deleted");
      } else toast.error(data.message || "Could not delete.");
    } catch {
      toast.error("Network error.");
    }
  };

  // ── bulk upload step 1: parse Excel/CSV → preview table (no save yet) ──
  const handleBulkFile = async (file) => {
    if (!file || bulkBusy) return;
    setBulkResult(null);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });
      if (!rows.length) {
        toast.error("File is empty or has no data rows.");
        return;
      }
      const cols = Object.keys(rows[0] || {});
      setPreviewRows(rows);
      setPreviewName(file.name || "file");
      setFileColumns(cols);
      setColMap(autoMapColumns(cols));
    } catch (err) {
      toast.error("Could not read the file. Use .xlsx or .csv.");
    }
  };

  // ── bulk upload step 2: SAVE the previewed rows to the DB ──
  const saveBulk = async () => {
    if (bulkBusy || !previewRows.length) return;
    if (!colMap.name || !colMap.mobile) {
      toast.error("Map the Customer Name and Mobile columns first.");
      return;
    }
    setBulkBusy(true);
    setBulkResult(null);
    try {
      // Apply the column mapping → canonical keys the backend understands.
      const mapped = previewRows.map((r) => ({
        Name: colMap.name ? r[colMap.name] : "",
        Mobile: colMap.mobile ? r[colMap.mobile] : "",
        Type: colMap.type ? r[colMap.type] : "",
        Area: colMap.area ? r[colMap.area] : "",
        District: colMap.district ? r[colMap.district] : "",
        Address: colMap.address ? r[colMap.address] : "",
      }));
      const res = await fetch(`${API_BASE}/api/customers/bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-auth-phone": phone },
        body: JSON.stringify({
          dealerPhone: phone,
          dealerProfile: getDealerProfile(),
          rows: mapped,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setBulkResult(data);
        setPreviewRows([]);
        setPreviewName("");
        setFileColumns([]);
        setColMap({});
        toast.success(`${data.added} customer(s) saved ✓`);
        load();
      } else {
        toast.error(data.message || "Save failed.");
      }
    } catch (err) {
      toast.error("Network error. Please try again.");
    } finally {
      setBulkBusy(false);
    }
  };

  // ── bulk upload: Google Sheet link ──
  const handleBulkSheet = async () => {
    if (bulkBusy) return;
    if (!sheetUrl.trim()) return toast.error("Paste a Google Sheet link.");
    setBulkBusy(true);
    setBulkResult(null);
    try {
      const res = await fetch(`${API_BASE}/api/customers/bulk-from-sheet`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-auth-phone": phone },
        body: JSON.stringify({
          dealerPhone: phone,
          dealerProfile: getDealerProfile(),
          sheetUrl: sheetUrl.trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setBulkResult(data);
        toast.success(`${data.added} customer(s) added ✓`);
        load();
      } else {
        toast.error(data.message || "Could not import from sheet.");
      }
    } catch {
      toast.error("Network error. Please try again.");
    } finally {
      setBulkBusy(false);
    }
  };

  // Download a CSV template with the expected headers + one sample row.
  const downloadTemplate = () => {
    const csv =
      "Name,Mobile,Type,District,Area,Address\n" +
      "Ramesh Bhai,9876543210,Mason,Surat,Adajan,Near Bus Stand\n";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "customers-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const custField = (label, k, opts = {}) => (
    <div>
      <label className="block text-xs font-bold text-gray-700 mb-1">
        {label} {opts.required && <span className="text-red-500">*</span>}
      </label>
      <input
        value={cust[k]}
        onChange={(e) => {
          let v = e.target.value;
          if (opts.onlyDigits) v = v.replace(/\D/g, "");
          else if (opts.noDigits) v = v.replace(/[0-9]/g, "");
          setC(k, v);
        }}
        placeholder={opts.placeholder || ""}
        inputMode={opts.inputMode}
        maxLength={opts.maxLength}
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
      />
    </div>
  );

  const TabBtn = ({ id, icon, count, children }) => (
    <button
      onClick={() => setView(id)}
      className={`flex-1 sm:flex-none flex items-center justify-center gap-1 px-2 sm:px-3 py-2 rounded-lg text-[11px] sm:text-xs font-bold transition whitespace-nowrap ${
        view === id
          ? "bg-gray-800 text-white shadow"
          : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-50"
      }`}
    >
      {icon} {children}
      {count != null && (
        <span
          className={`ml-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-extrabold leading-none ${
            view === id ? "bg-white/25 text-white" : "bg-gray-100 text-gray-600"
          }`}
        >
          {count}
        </span>
      )}
    </button>
  );

  return (
    <div className="w-full px-4 md:px-8 pt-16 md:pt-24 pb-28 md:pb-10">
      {/* Header */}
      <div className="flex items-center gap-2.5 mb-3">
        <button
          onClick={() => navigate("/dashboard")}
          className="p-1.5 rounded-full bg-white border border-gray-200 hover:bg-gray-50"
          title="Back to dashboard"
        >
          <ArrowBackIcon sx={{ fontSize: 18 }} />
        </button>
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-yellow-500 to-orange-600 text-white flex items-center justify-center shadow text-lg">
          🤝
        </div>
        <div>
          <h1 className="text-lg font-extrabold text-gray-800 leading-tight">
            My Customers
          </h1>
          <p className="text-xs text-gray-500">Manage your customers & their sales</p>
        </div>
      </div>

      {/* Tabs + (on dashboard) stat pills — sab ek hi line me. */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <TabBtn id="dashboard" icon={<DashboardIcon sx={{ fontSize: 16 }} />}>
          Dashboard
        </TabBtn>
        <TabBtn
          id="customers"
          icon={<GroupsIcon sx={{ fontSize: 16 }} />}
          count={stats.totalCustomers}
        >
          Customers
        </TabBtn>
        <TabBtn
          id="sales"
          icon={<AddchartIcon sx={{ fontSize: 16 }} />}
          count={stats.entries}
        >
          Sales
        </TabBtn>
      </div>

      {!phone ? (
        <div className="text-center py-16 text-gray-500">
          Could not detect your login number. Please log in again.
        </div>
      ) : loading ? (
        <div className="text-center py-16 text-gray-400">Loading…</div>
      ) : (
        <>
          {/* ───────── DASHBOARD ───────── */}
          {view === "dashboard" && (
            <>
              {/* Dashboard = Rewards only + progress to next reward */}
              <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                🎁 Reward Details
              </h3>
              {!registered ? (
                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 text-center text-gray-500 text-sm">
                  Rewards registered dealers ko dikhte hain.
                </div>
              ) : rewards.length === 0 ? (
                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 text-center text-gray-500 text-sm">
                  No rewards assigned yet. Admin will set your reward tiers.
                </div>
              ) : (
                <>
                  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                    <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-amber-50 text-amber-800 sticky top-0">
                          <tr className="text-left">
                            <th className="px-4 py-3 font-bold whitespace-nowrap">Target (KG)</th>
                            <th className="px-4 py-3 font-bold">Reward</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {[...rewards]
                            .sort((a, b) => (a.fromKg || 0) - (b.fromKg || 0))
                            .map((r, i) => {
                              const ach = Number(stats.totalSales || 0);
                              const reached = ach >= Number(r.fromKg || 0);
                              return (
                                <tr key={i} className={reached ? "bg-emerald-50/50" : "hover:bg-amber-50/40"}>
                                  <td className="px-4 py-2.5 font-semibold text-gray-900 whitespace-nowrap">
                                    {reached && <span className="mr-1">✅</span>}
                                    {r.toKg
                                      ? `${Number(r.fromKg).toLocaleString()} – ${Number(r.toKg).toLocaleString()} KG`
                                      : `${Number(r.fromKg).toLocaleString()}+ KG`}
                                  </td>
                                  <td className="px-4 py-2.5 text-yellow-700 font-semibold">
                                    {(r.rewards || []).filter(Boolean).join(", ") || "—"}
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Progress to next reward (based on total sales) */}
                  {(() => {
                    const tiers = [...rewards].sort((a, b) => (a.fromKg || 0) - (b.fromKg || 0));
                    const ach = Number(stats.totalSales || 0);
                    const next = tiers.find((t) => Number(t.fromKg || 0) > ach);
                    if (!next) {
                      return (
                        <p className="mt-3 text-sm font-semibold text-emerald-700">
                          🎉 Aapne top reward tier achieve kar liya!
                        </p>
                      );
                    }
                    const prevFrom =
                      [...tiers].reverse().find((t) => Number(t.fromKg || 0) <= ach)?.fromKg || 0;
                    const span = Math.max(1, Number(next.fromKg) - prevFrom);
                    const pct = Math.min(100, Math.max(0, ((ach - prevFrom) / span) * 100));
                    const gap = Math.max(0, Number(next.fromKg) - ach);
                    const nextReward =
                      (next.rewards || []).filter(Boolean).join(", ") || "next reward";
                    return (
                      <div className="mt-4">
                        <div className="flex items-center justify-between text-[11px] font-bold text-gray-500 mb-1">
                          <span>{ach.toLocaleString()} KG</span>
                          <span>{Number(next.fromKg).toLocaleString()} KG</span>
                        </div>
                        <div className="w-full h-3 rounded-full bg-gray-200 overflow-hidden">
                          <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
                        </div>
                        <p className="mt-2 text-sm text-gray-700 flex items-center gap-1 flex-wrap">
                          <span className="font-extrabold text-emerald-700">{gap.toLocaleString()} KG</span>
                          <span>aur</span>
                          <span aria-hidden>→</span>
                          <span>reward:</span>
                          <span className="font-bold text-yellow-700">{nextReward}</span>
                        </p>
                      </div>
                    );
                  })()}
                </>
              )}
            </>
          )}

          {/* ───────── MY CUSTOMERS ───────── */}
          {view === "customers" && (
            <>
              <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                <div className="relative flex-1 min-w-[200px]">
                  <SearchRoundedIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by name, mobile or type…"
                    className="w-full pl-11 pr-4 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={openAddCustomer}
                    className="flex items-center gap-2 bg-gray-800 hover:bg-gray-900 text-white font-bold px-4 py-2.5 rounded-xl shadow transition"
                  >
                    <PersonAddAlt1Icon fontSize="small" /> Add Customer
                  </button>
                  <button
                    onClick={() => {
                      setBulkResult(null);
                      setSheetUrl("");
                      setPreviewRows([]);
                      setPreviewName("");
                      setFileColumns([]);
                      setColMap({});
                      setShowBulk(true);
                    }}
                    className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-4 py-2.5 rounded-xl shadow transition"
                  >
                    <UploadFileIcon fontSize="small" /> Bulk Upload
                  </button>
                  <button
                    onClick={() => openSale("")}
                    className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-white font-bold px-4 py-2.5 rounded-xl shadow transition"
                  >
                    <AddchartIcon fontSize="small" /> Add Sale
                  </button>

                  {/* AI — photo se customer (preview ke baad add) */}
                  <button
                    onClick={() => !aiBusy && aiFileRef.current?.click()}
                    disabled={aiBusy}
                    className="flex items-center gap-2 bg-gradient-to-r from-fuchsia-600 to-purple-600 hover:from-fuchsia-700 hover:to-purple-700 disabled:opacity-60 text-white font-bold px-4 py-2.5 rounded-xl shadow transition"
                    title="Photo upload karo — AI customer details nikaal ke dikhayega, review karke save"
                  >
                    <AutoAwesomeIcon fontSize="small" />
                    {aiBusy ? "Reading…" : "AI Add (Photo)"}
                  </button>
                  <input
                    ref={aiFileRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={(e) => handleAiPhoto(e.target.files?.[0])}
                  />
                </div>
              </div>

              {filtered.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-gray-300">
                  <GroupsIcon style={{ fontSize: 56 }} className="text-gray-300" />
                  <p className="mt-3 text-gray-500 font-semibold">
                    {customers.length === 0
                      ? "No customers yet. Add your first customer."
                      : "No customers match your search."}
                  </p>
                </div>
              ) : (
                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-gray-600 sticky top-0">
                        <tr className="text-left">
                          <th className="px-3 py-3 font-bold">#</th>
                          <th className="px-3 py-3 font-bold">Name</th>
                          <th className="px-3 py-3 font-bold">Mobile</th>
                          <th className="px-3 py-3 font-bold">Type</th>
                          <th className="px-3 py-3 font-bold hidden sm:table-cell">
                            Area / District
                          </th>
                          <th className="px-3 py-3 font-bold text-right">Sales</th>
                          <th className="px-3 py-3 font-bold text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {pagedCustomers.map((c, i) => {
                          const kg = (c.sales || []).reduce(
                            (s, x) => s + (Number(x.quantity) || 0),
                            0
                          );
                          return (
                            <tr
                              key={c._id}
                              onClick={() => setDetailCust(c)}
                              className="hover:bg-gray-50 transition cursor-pointer"
                            >
                              <td className="px-3 py-2.5 text-gray-400">
                                {(pageSafe - 1) * PAGE_SIZE + i + 1}
                              </td>
                              <td className="px-3 py-2.5">
                                <div className="flex items-center gap-2.5">
                                  {c.profilePic ? (
                                    <img
                                      src={c.profilePic}
                                      alt=""
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setLightboxImg(c.profilePic);
                                      }}
                                      className="w-8 h-8 rounded-full object-cover shrink-0 border border-gray-200 cursor-zoom-in"
                                    />
                                  ) : (
                                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-yellow-500 to-orange-600 text-white flex items-center justify-center font-bold text-sm shrink-0">
                                      {String(c.name || "?")[0]?.toUpperCase()}
                                    </div>
                                  )}
                                  <span className="font-bold text-gray-800">
                                    {c.name}
                                  </span>
                                  {c.documents?.aadhar && (
                                    <img
                                      src={c.documents.aadhar}
                                      alt="Aadhaar"
                                      title="Aadhaar card"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setLightboxImg(c.documents.aadhar);
                                      }}
                                      className="w-10 h-7 rounded object-cover shrink-0 border border-gray-300 cursor-zoom-in"
                                    />
                                  )}
                                </div>
                              </td>
                              <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">
                                +91 {c.mobile}
                              </td>
                              <td className="px-3 py-2.5">
                                {c.type ? (
                                  <span className="text-xs font-bold bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full whitespace-nowrap">
                                    {c.type}
                                  </span>
                                ) : (
                                  <span className="text-gray-300">-</span>
                                )}
                              </td>
                              <td className="px-3 py-2.5 text-gray-600 hidden sm:table-cell">
                                {[c.area, c.district].filter(Boolean).join(", ") || "-"}
                              </td>
                              <td className="px-3 py-2.5 text-right font-bold text-emerald-700 whitespace-nowrap">
                                {kg.toLocaleString()} KG
                              </td>
                              <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                                <div className="flex items-center justify-center gap-1">
                                  <button
                                    onClick={() => openSale(c._id)}
                                    title="Add Sale"
                                    className="flex items-center gap-1 text-xs font-bold text-white bg-yellow-500 hover:bg-yellow-600 rounded-lg px-2.5 py-1.5 transition whitespace-nowrap"
                                  >
                                    <AddchartIcon sx={{ fontSize: 16 }} /> Sale
                                  </button>
                                  <button
                                    onClick={() => openEditCustomer(c)}
                                    title="Edit customer"
                                    className="text-gray-400 hover:text-blue-600 transition p-1"
                                  >
                                    <EditOutlinedIcon fontSize="small" />
                                  </button>
                                  <button
                                    onClick={() => deleteCustomer(c._id)}
                                    title="Remove"
                                    className="text-gray-300 hover:text-red-500 transition p-1"
                                  >
                                    <DeleteOutlineIcon fontSize="small" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Pager */}
                  {custTotalPages > 1 && (
                    <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-gray-100 bg-gray-50">
                      <span className="text-xs text-gray-500">
                        {(pageSafe - 1) * PAGE_SIZE + 1}–
                        {Math.min(pageSafe * PAGE_SIZE, filtered.length)} of {filtered.length}
                      </span>
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => setCustPage((p) => Math.max(1, p - 1))}
                          disabled={pageSafe <= 1}
                          className="px-3 py-1.5 rounded-lg text-xs font-bold border border-gray-200 bg-white hover:bg-gray-100 disabled:opacity-40"
                        >
                          ‹ Prev
                        </button>
                        <span className="text-xs font-bold text-gray-700 px-1">
                          {pageSafe} / {custTotalPages}
                        </span>
                        <button
                          onClick={() => setCustPage((p) => Math.min(custTotalPages, p + 1))}
                          disabled={pageSafe >= custTotalPages}
                          className="px-3 py-1.5 rounded-lg text-xs font-bold border border-gray-200 bg-white hover:bg-gray-100 disabled:opacity-40"
                        >
                          Next ›
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* ───────── CUSTOMERS SALES ───────── */}
          {view === "sales" && (
            <>
              <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                <h3 className="font-bold text-gray-800">Customer-wise Sales</h3>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={openAddCustomer}
                    className="flex items-center gap-2 bg-gray-800 hover:bg-gray-900 text-white font-bold px-4 py-2.5 rounded-xl shadow transition"
                  >
                    <PersonAddAlt1Icon fontSize="small" /> Add Customer
                  </button>
                  <button
                    onClick={() => {
                      setBulkResult(null);
                      setSheetUrl("");
                      setPreviewRows([]);
                      setPreviewName("");
                      setFileColumns([]);
                      setColMap({});
                      setShowBulk(true);
                    }}
                    className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-4 py-2.5 rounded-xl shadow transition"
                  >
                    <UploadFileIcon fontSize="small" /> Bulk Upload
                  </button>
                  <button
                    onClick={() => openSale("")}
                    className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-white font-bold px-4 py-2.5 rounded-xl shadow transition"
                  >
                    <AddchartIcon fontSize="small" /> Add Sale
                  </button>

                </div>
              </div>

              {/* Small rewards strip — next-reward progress */}
              {registered && rewards.length > 0 && (() => {
                const tiers = [...rewards].sort((a, b) => (a.fromKg || 0) - (b.fromKg || 0));
                const ach = Number(stats.totalSales || 0);
                const next = tiers.find((t) => Number(t.fromKg || 0) > ach);
                const prevFrom =
                  [...tiers].reverse().find((t) => Number(t.fromKg || 0) <= ach)?.fromKg || 0;
                const span = next ? Math.max(1, Number(next.fromKg) - prevFrom) : 1;
                const pct = next ? Math.min(100, Math.max(0, ((ach - prevFrom) / span) * 100)) : 100;
                const gap = next ? Math.max(0, Number(next.fromKg) - ach) : 0;
                const nextReward = next
                  ? (next.rewards || []).filter(Boolean).join(", ") || "next reward"
                  : "";
                return (
                  <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                    <div className="flex items-center justify-between text-xs font-bold text-amber-800 mb-1">
                      <span>🎁 Rewards</span>
                      <span>{ach.toLocaleString()} KG</span>
                    </div>
                    {next ? (
                      <>
                        <div className="w-full h-2 rounded-full bg-amber-100 overflow-hidden">
                          <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
                        </div>
                        <p className="mt-1 text-[11px] text-gray-700">
                          <span className="font-extrabold text-emerald-700">
                            {gap.toLocaleString()} KG
                          </span>{" "}
                          aur → <span className="font-semibold text-yellow-700">{nextReward}</span>
                        </p>
                      </>
                    ) : (
                      <p className="text-[11px] font-semibold text-emerald-700">
                        🎉 Top reward achieved!
                      </p>
                    )}
                  </div>
                );
              })()}

              {customers.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-gray-300">
                  <p className="text-gray-500 font-semibold">No customers yet.</p>
                </div>
              ) : (
                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-gray-600 sticky top-0">
                        <tr className="text-left">
                          <th className="px-3 py-3 font-bold">#</th>
                          <th className="px-3 py-3 font-bold">Customer</th>
                          <th className="px-3 py-3 font-bold text-center">Entries</th>
                          <th className="px-3 py-3 font-bold text-right">Total Sales</th>
                          {registered && rewards.length > 0 && (
                            <th className="px-3 py-3 font-bold">Reward (as per sale)</th>
                          )}
                          <th className="px-3 py-3 font-bold text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {customers.map((c, i) => {
                          const sales = [...(c.sales || [])].sort(
                            (a, b) => new Date(b.date) - new Date(a.date)
                          );
                          const kg = sales.reduce((s, x) => s + (Number(x.quantity) || 0), 0);
                          // Show every customer's sales inline (always expanded).
                          const isOpen = true;
                          return (
                            <Fragment key={c._id}>
                              <tr className="hover:bg-gray-50 transition">
                                <td className="px-3 py-2.5 text-gray-400">{i + 1}</td>
                                <td className="px-3 py-2.5">
                                  <div className="flex items-center gap-2.5">
                                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-yellow-500 to-orange-600 text-white flex items-center justify-center font-bold text-sm shrink-0">
                                      {String(c.name || "?")[0]?.toUpperCase()}
                                    </div>
                                    <div className="min-w-0">
                                      <p className="font-bold text-gray-800 truncate">{c.name}</p>
                                      <p className="text-xs text-gray-500">+91 {c.mobile}</p>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-3 py-2.5 text-center text-gray-700">
                                  {sales.length}
                                </td>
                                <td className="px-3 py-2.5 text-right font-bold text-emerald-700 whitespace-nowrap">
                                  {kg.toLocaleString()} KG
                                </td>
                                {registered && rewards.length > 0 && (() => {
                                  const rw = rewardForKg(kg);
                                  return (
                                    <td className="px-3 py-2.5">
                                      {rw.current ? (
                                        <span className="text-xs font-bold bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full">
                                          🎁 {rw.current}
                                        </span>
                                      ) : (
                                        <span className="text-xs text-gray-400">—</span>
                                      )}
                                      {rw.next && (
                                        <div className="text-[10px] text-gray-500 mt-0.5">
                                          {rw.gap.toLocaleString()} KG → {rw.next}
                                        </div>
                                      )}
                                    </td>
                                  );
                                })()}
                                <td className="px-3 py-2.5">
                                  <div className="flex items-center justify-center gap-1">
                                    <button
                                      onClick={() => openSale(String(c._id))}
                                      title="Add Sale"
                                      className="flex items-center gap-1 text-xs font-bold text-white bg-yellow-500 hover:bg-yellow-600 rounded-lg px-2.5 py-1.5 transition whitespace-nowrap"
                                    >
                                      <AddchartIcon sx={{ fontSize: 16 }} /> Sale
                                    </button>
                                  </div>
                                </td>
                              </tr>

                              {isOpen && (
                                <tr>
                                  <td colSpan={registered && rewards.length > 0 ? 6 : 5} className="bg-gray-50 px-3 py-3">
                                    {sales.length === 0 ? (
                                      <p className="text-sm text-gray-400 text-center py-2">
                                        No sales yet for {c.name}.
                                      </p>
                                    ) : (
                                      <div className="rounded-xl border border-gray-200 overflow-hidden bg-white">
                                        <table className="w-full text-xs">
                                          <thead className="bg-gray-100 text-gray-600">
                                            <tr className="text-left">
                                              <th className="px-3 py-2 font-bold">Date</th>
                                              <th className="px-3 py-2 font-bold text-right">Qty (KG)</th>
                                              <th className="px-3 py-2 font-bold">Remarks</th>
                                              <th className="px-3 py-2 font-bold text-center">Action</th>
                                            </tr>
                                          </thead>
                                          <tbody className="divide-y divide-gray-100">
                                            {sales.map((s) => (
                                              <tr key={s._id} className="hover:bg-gray-50">
                                                <td className="px-3 py-2 whitespace-nowrap">{fmtDate(s.date)}</td>
                                                <td className="px-3 py-2 text-right font-bold text-gray-800 whitespace-nowrap">
                                                  {Number(s.quantity).toLocaleString()}
                                                </td>
                                                <td className="px-3 py-2 text-gray-600">{s.remarks || "—"}</td>
                                                <td className="px-3 py-2">
                                                  <div className="flex items-center justify-center gap-2">
                                                    <button
                                                      onClick={() => openEditSale(c._id, s)}
                                                      className="text-gray-400 hover:text-blue-600 transition"
                                                      title="Edit sale"
                                                    >
                                                      <EditOutlinedIcon fontSize="small" />
                                                    </button>
                                                    <button
                                                      onClick={() => deleteSale(String(c._id), String(s._id))}
                                                      className="text-gray-300 hover:text-red-500 transition"
                                                      title="Delete sale"
                                                    >
                                                      <DeleteOutlineIcon fontSize="small" />
                                                    </button>
                                                  </div>
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              )}
                            </Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ───────── AI preview modal (review before save) ───────── */}
      {showAiPreview && (
        <div
          className="fixed inset-0 bg-black/50 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => !aiSaving && setShowAiPreview(false)}
        >
          <div
            className="bg-white w-full sm:max-w-4xl rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b sticky top-0 bg-white rounded-t-3xl z-10">
              <div>
                <h2 className="text-lg font-extrabold text-gray-800 flex items-center gap-2">
                  <AutoAwesomeIcon sx={{ fontSize: 20 }} /> AI ne ye customers padhe
                </h2>
                <p className="text-xs text-gray-500">
                  Edit karke ya galat row hata ke Save karo. Bina valid mobile waali row skip hogi.
                </p>
              </div>
              <button
                onClick={() => !aiSaving && setShowAiPreview(false)}
                className="p-2 rounded-full hover:bg-gray-100"
              >
                <CloseIcon />
              </button>
            </div>

            <div className="p-4">
              {aiRows.length === 0 ? (
                <div className="py-10 text-center text-gray-500 text-sm">Koi row nahi.</div>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-gray-200">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr className="text-left">
                        <th className="px-2 py-2 font-bold">Name</th>
                        <th className="px-2 py-2 font-bold">Mobile</th>
                        <th className="px-2 py-2 font-bold">Type</th>
                        <th className="px-2 py-2 font-bold">Area</th>
                        <th className="px-2 py-2 font-bold">District</th>
                        <th className="px-2 py-2 font-bold">Address</th>
                        <th className="px-2 py-2 font-bold"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {aiRows.map((r, i) => {
                        const okMobile = /^[6-9]\d{9}$/.test(
                          String(r.mobile).replace(/\D/g, "").slice(-10)
                        );
                        return (
                          <tr key={i} className="align-top">
                            <td className="px-1.5 py-1.5">
                              <input
                                value={r.name}
                                onChange={(e) => setAiRow(i, "name", e.target.value)}
                                className="w-32 px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                              />
                            </td>
                            <td className="px-1.5 py-1.5">
                              <input
                                value={r.mobile}
                                onChange={(e) =>
                                  setAiRow(i, "mobile", e.target.value.replace(/\D/g, "").slice(0, 10))
                                }
                                inputMode="numeric"
                                className={`w-28 px-2 py-1.5 text-xs border rounded-lg focus:outline-none focus:ring-2 ${
                                  okMobile
                                    ? "border-gray-300 focus:ring-yellow-500"
                                    : "border-red-400 focus:ring-red-300"
                                }`}
                              />
                            </td>
                            <td className="px-1.5 py-1.5">
                              <select
                                value={r.type}
                                onChange={(e) => setAiRow(i, "type", e.target.value)}
                                className="w-32 px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                              >
                                <option value="">—</option>
                                {CUSTOMER_TYPES.map((t) => (
                                  <option key={t} value={t}>
                                    {t}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className="px-1.5 py-1.5">
                              <input
                                value={r.area}
                                onChange={(e) => setAiRow(i, "area", e.target.value)}
                                className="w-28 px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                              />
                            </td>
                            <td className="px-1.5 py-1.5">
                              <input
                                value={r.district}
                                onChange={(e) => setAiRow(i, "district", e.target.value)}
                                className="w-28 px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                              />
                            </td>
                            <td className="px-1.5 py-1.5">
                              <input
                                value={r.address}
                                onChange={(e) => setAiRow(i, "address", e.target.value)}
                                className="w-40 px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                              />
                            </td>
                            <td className="px-1.5 py-1.5">
                              <button
                                onClick={() => removeAiRow(i)}
                                className="p-1.5 rounded-lg text-red-600 hover:bg-red-50"
                                title="Remove"
                              >
                                <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-3 px-5 py-4 border-t sticky bottom-0 bg-white">
              <span className="text-xs text-gray-500">{aiRows.length} row(s)</span>
              <div className="flex gap-2">
                <button
                  onClick={() => !aiSaving && setShowAiPreview(false)}
                  className="px-4 py-2 rounded-lg text-sm font-bold bg-gray-100 text-gray-700 hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={saveAiRows}
                  disabled={aiSaving || aiRows.length === 0}
                  className="px-4 py-2 rounded-lg text-sm font-bold bg-gray-800 text-white hover:bg-gray-900 disabled:opacity-60"
                >
                  {aiSaving ? "Saving…" : "Save Customers"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ───────── Add Customer modal ───────── */}
      {showCust && (
        <div
          className="fixed inset-0 bg-black/50 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => !savingCust && setShowCust(false)}
        >
          <div
            className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b sticky top-0 bg-white rounded-t-3xl">
              <h2 className="text-lg font-extrabold text-gray-800">
                {editCustId ? "Edit Customer" : "Add Customer"}
              </h2>
              <button
                onClick={() => !savingCust && setShowCust(false)}
                className="p-1.5 bg-gray-100 rounded-full hover:bg-gray-200"
              >
                <CloseIcon fontSize="small" />
              </button>
            </div>
            <form onSubmit={submitCustomer} className="p-5 space-y-3">
              {custField("Customer Name", "name", { required: true, noDigits: true, placeholder: "Full name" })}
              {custField("Mobile Number", "mobile", {
                required: true,
                onlyDigits: true,
                inputMode: "numeric",
                maxLength: 10,
                placeholder: "10-digit mobile",
              })}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  Customer Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={cust.type}
                  onChange={(e) => setC("type", e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                >
                  <option value="">-- Select Type --</option>
                  {CUSTOMER_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {custField("District", "district", { placeholder: "optional" })}
                {custField("Area", "area", { placeholder: "optional" })}
              </div>
              {custField("Address", "address", { placeholder: "optional" })}

              {/* Optional photos — User photo + Aadhaar card */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    User Photo{" "}
                    <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  {cust.profilePic ? (
                    <div className="relative">
                      <img
                        src={cust.profilePic}
                        alt="user"
                        className="w-full h-28 object-cover rounded-lg border border-gray-300"
                      />
                      <button
                        type="button"
                        onClick={() => setC("profilePic", "")}
                        className="absolute top-1 right-1 bg-black/60 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs"
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center gap-0.5 h-28 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-yellow-500 text-gray-500 text-xs">
                      <span className="text-2xl">📷</span>
                      Upload
                      <input
                        type="file"
                        accept="image/*"
                        capture="user"
                        className="hidden"
                        onChange={(e) => pickCustPhoto("profilePic", e.target.files?.[0])}
                      />
                    </label>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    Aadhaar Card{" "}
                    <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  {cust.aadhar ? (
                    <div className="relative">
                      <img
                        src={cust.aadhar}
                        alt="aadhaar"
                        className="w-full h-28 object-cover rounded-lg border border-gray-300"
                      />
                      <button
                        type="button"
                        onClick={() => setC("aadhar", "")}
                        className="absolute top-1 right-1 bg-black/60 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs"
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center gap-0.5 h-28 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-yellow-500 text-gray-500 text-xs">
                      <span className="text-2xl">🪪</span>
                      Upload
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={(e) => pickCustPhoto("aadhar", e.target.files?.[0])}
                      />
                    </label>
                  )}
                </div>
              </div>

              <button
                type="submit"
                disabled={savingCust}
                className="w-full mt-2 bg-gray-800 text-white font-bold py-3 rounded-xl hover:bg-gray-900 disabled:opacity-50 transition"
              >
                {savingCust ? "Saving…" : editCustId ? "Update Customer" : "Save Customer"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ───────── Add Sale modal ───────── */}
      {showSale && (
        <div
          className="fixed inset-0 bg-black/50 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => !savingSale && setShowSale(false)}
        >
          <div
            className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b sticky top-0 bg-white rounded-t-3xl">
              <h2 className="text-lg font-extrabold text-gray-800">
                {editSaleId ? "Edit Sale" : "Add Sale"}
              </h2>
              <button
                onClick={() => !savingSale && setShowSale(false)}
                className="p-1.5 bg-gray-100 rounded-full hover:bg-gray-200"
              >
                <CloseIcon fontSize="small" />
              </button>
            </div>
            <form onSubmit={submitSale} className="p-5 space-y-3">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  Customer <span className="text-red-500">*</span>
                </label>
                <select
                  value={saleCustomerId}
                  onChange={(e) => setSaleCustomerId(e.target.value)}
                  disabled={!!editSaleId}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white disabled:bg-gray-100 disabled:text-gray-600 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                >
                  <option value="">-- Select Customer --</option>
                  {customers.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.name} (+91 {c.mobile})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  Sale Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={sale.date}
                  max={todayStr()}
                  onChange={(e) => setS("date", e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  Quantity (KG) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={sale.quantity}
                  onChange={(e) => setS("quantity", e.target.value)}
                  placeholder="e.g. 500"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Remarks</label>
                <input
                  value={sale.remarks}
                  onChange={(e) => setS("remarks", e.target.value)}
                  placeholder="optional"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>
              <button
                type="submit"
                disabled={savingSale}
                className="w-full mt-2 bg-yellow-500 text-white font-bold py-3 rounded-xl hover:bg-yellow-600 disabled:opacity-50 transition"
              >
                {savingSale ? "Saving…" : editSaleId ? "Update Sale" : "Save Sale"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ───────── Bulk Upload modal ───────── */}
      {showBulk && (
        <div
          className="fixed inset-0 bg-black/50 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => !bulkBusy && setShowBulk(false)}
        >
          <div
            className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b sticky top-0 bg-white rounded-t-3xl">
              <h2 className="text-lg font-extrabold text-gray-800">
                Bulk Upload Customers
              </h2>
              <button
                onClick={() => !bulkBusy && setShowBulk(false)}
                className="p-1.5 bg-gray-100 rounded-full hover:bg-gray-200"
              >
                <CloseIcon fontSize="small" />
              </button>
            </div>

            <div className="p-5 space-y-5">
              {/* Expected columns */}
              <div className="bg-gray-50 rounded-xl p-3 text-xs text-gray-600">
                <p className="font-bold text-gray-700 mb-1">
                  Required columns (first row = headers):
                </p>
                <p>
                  <b>Name</b>, <b>Mobile</b> &nbsp;(zaroori) · Type, District,
                  Area, Address (optional)
                </p>
                <button
                  onClick={downloadTemplate}
                  className="mt-2 text-emerald-700 font-bold hover:underline"
                >
                  ↓ Download template (.csv)
                </button>
              </div>

              {/* Option A: file */}
              <div>
                <p className="text-sm font-bold text-gray-800 mb-2">
                  1. Upload Excel / CSV file
                </p>
                <label
                  className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-xl px-4 py-6 cursor-pointer transition ${
                    bulkBusy
                      ? "opacity-50 pointer-events-none"
                      : "border-emerald-300 hover:bg-emerald-50"
                  }`}
                >
                  <UploadFileIcon className="text-emerald-600" />
                  <span className="text-sm font-semibold text-gray-700">
                    Choose .xlsx, .xls or .csv
                  </span>
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = "";
                      handleBulkFile(f);
                    }}
                  />
                </label>
              </div>

              {/* Map your columns + summary */}
              {previewRows.length > 0 &&
                (() => {
                  const val = (r, key) => {
                    const col = colMap[key];
                    return col ? String(r[col] ?? "").trim() : "";
                  };
                  const existing = new Set(
                    customers.map((c) => String(c.mobile).replace(/\D/g, "").slice(-10))
                  );
                  const seen = new Set();
                  let willAdd = 0, duplicates = 0, errors = 0;
                  previewRows.forEach((r) => {
                    const name = val(r, "name");
                    const mob = val(r, "mobile").replace(/\D/g, "").slice(-10);
                    const ok = !!name && !/[0-9]/.test(name) && /^[6-9]\d{9}$/.test(mob);
                    if (!ok) { errors++; return; }
                    if (existing.has(mob) || seen.has(mob)) { duplicates++; return; }
                    seen.add(mob);
                    willAdd++;
                  });
                  const mapReady = !!colMap.name && !!colMap.mobile;
                  return (
                    <div className="border border-gray-200 rounded-xl p-3 bg-slate-50/50">
                      <p className="text-sm font-extrabold text-gray-800">Map your columns</p>
                      <p className="text-[11px] text-gray-500 mb-3">
                        {previewName} · {previewRows.length} rows. Galat mapping fix karein.
                      </p>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {FIELD_DEFS.map((f) => (
                          <div key={f.key}>
                            <label className="block text-xs font-bold text-gray-700 mb-1">
                              {f.label}{" "}
                              {f.required ? (
                                <span className="text-red-500">*</span>
                              ) : (
                                <span className="text-gray-400 font-normal">(optional)</span>
                              )}
                            </label>
                            <select
                              value={colMap[f.key] || ""}
                              onChange={(e) =>
                                setColMap((m) => ({ ...m, [f.key]: e.target.value }))
                              }
                              className={`w-full px-3 py-2 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                                f.required && !colMap[f.key]
                                  ? "border-red-300"
                                  : "border-gray-300"
                              }`}
                            >
                              <option value="">— Not mapped —</option>
                              {fileColumns.map((c) => (
                                <option key={c} value={c}>
                                  {c}
                                </option>
                              ))}
                            </select>
                            <p className="text-[11px] text-gray-400 mt-0.5">e.g. {f.eg}</p>
                          </div>
                        ))}
                      </div>

                      {/* Summary chips */}
                      <div className="flex flex-wrap items-center gap-2 mt-3 text-xs font-bold">
                        <span className="px-2.5 py-1 rounded-full bg-gray-100 text-gray-700">
                          Total: {previewRows.length}
                        </span>
                        <span className="px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                          Will add: {willAdd}
                        </span>
                        <span className="px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                          Duplicates: {duplicates}
                        </span>
                        <span className="px-2.5 py-1 rounded-full bg-rose-50 text-rose-700 border border-rose-200">
                          Errors: {errors}
                        </span>
                      </div>

                      <div className="flex items-center gap-2 mt-3">
                        <button
                          onClick={() => {
                            setPreviewRows([]);
                            setPreviewName("");
                            setFileColumns([]);
                            setColMap({});
                          }}
                          disabled={bulkBusy}
                          className="px-3 py-2 text-sm font-bold text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
                        >
                          Clear
                        </button>
                        <button
                          onClick={saveBulk}
                          disabled={bulkBusy || !mapReady || willAdd === 0}
                          className="flex-1 bg-emerald-600 text-white font-bold py-2.5 rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition"
                        >
                          {bulkBusy
                            ? "Saving…"
                            : !mapReady
                              ? "Map Name & Mobile"
                              : `Save ${willAdd} Customer${willAdd === 1 ? "" : "s"}`}
                        </button>
                      </div>
                    </div>
                  );
                })()}

              <div className="flex items-center gap-3 text-xs text-gray-400">
                <div className="flex-1 h-px bg-gray-200" />
                OR
                <div className="flex-1 h-px bg-gray-200" />
              </div>

              {/* Option B: google sheet */}
              <div>
                <p className="text-sm font-bold text-gray-800 mb-2">
                  2. Paste a Google Sheet link
                </p>
                <input
                  value={sheetUrl}
                  onChange={(e) => setSheetUrl(e.target.value)}
                  placeholder="https://docs.google.com/spreadsheets/d/..."
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <p className="text-[11px] text-gray-400 mt-1">
                  Sheet ko “Anyone with the link can view” par share karein.
                </p>
                <button
                  onClick={handleBulkSheet}
                  disabled={bulkBusy}
                  className="mt-2 w-full bg-emerald-600 text-white font-bold py-2.5 rounded-xl hover:bg-emerald-700 disabled:opacity-50 transition"
                >
                  {bulkBusy ? "Importing…" : "Import from Sheet"}
                </button>
              </div>

              {/* Result */}
              {bulkResult && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-sm">
                  <p className="font-bold text-emerald-800">
                    ✓ {bulkResult.added} added
                  </p>
                  <p className="text-gray-600 text-xs mt-1">
                    {bulkResult.duplicates || 0} duplicate(s) skipped ·{" "}
                    {bulkResult.invalid || 0} invalid row(s) · of{" "}
                    {bulkResult.total} total
                  </p>
                  {Array.isArray(bulkResult.errors) &&
                    bulkResult.errors.length > 0 && (
                      <p className="text-[11px] text-gray-500 mt-1">
                        e.g. row {bulkResult.errors[0].row}:{" "}
                        {bulkResult.errors[0].reason}
                      </p>
                    )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ───────── Customer detail modal (photo + aadhaar + sales) ───────── */}
      {detailCust && (() => {
        const c = detailCust;
        const sales = [...(c.sales || [])].sort(
          (a, b) => new Date(b.date) - new Date(a.date)
        );
        const kg = sales.reduce((s, x) => s + (Number(x.quantity) || 0), 0);
        const aadhar = c.documents?.aadhar || "";
        return (
          <div
            className="fixed inset-0 bg-black/50 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4"
            onClick={() => setDetailCust(null)}
          >
            <div
              className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[92vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b sticky top-0 bg-white rounded-t-3xl z-10">
                <h2 className="text-lg font-extrabold text-gray-800">Customer Details</h2>
                <button
                  onClick={() => setDetailCust(null)}
                  className="p-1.5 bg-gray-100 rounded-full hover:bg-gray-200"
                >
                  <CloseIcon fontSize="small" />
                </button>
              </div>

              <div className="p-5 space-y-4">
                {/* Identity */}
                <div className="flex items-center gap-3">
                  {c.profilePic ? (
                    <img
                      src={c.profilePic}
                      alt=""
                      onClick={() => setLightboxImg(c.profilePic)}
                      className="w-16 h-16 rounded-full object-cover border border-gray-200 cursor-zoom-in"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-yellow-500 to-orange-600 text-white flex items-center justify-center font-extrabold text-2xl shrink-0">
                      {String(c.name || "?")[0]?.toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="text-lg font-extrabold text-gray-900 truncate">{c.name}</p>
                    <p className="text-sm text-gray-600">+91 {c.mobile}</p>
                    {c.type && (
                      <span className="inline-block mt-1 text-[11px] font-bold bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full">
                        {c.type}
                      </span>
                    )}
                  </div>
                </div>

                {/* Address details */}
                {(c.area || c.district || c.address) && (
                  <div className="bg-gray-50 rounded-xl p-3 text-sm text-gray-700 space-y-1">
                    {(c.area || c.district) && (
                      <p>
                        <span className="font-semibold text-gray-500">Area / District: </span>
                        {[c.area, c.district].filter(Boolean).join(", ")}
                      </p>
                    )}
                    {c.address && (
                      <p>
                        <span className="font-semibold text-gray-500">Address: </span>
                        {c.address}
                      </p>
                    )}
                  </div>
                )}

                {/* Photos */}
                <div>
                  <p className="text-xs font-bold text-gray-700 mb-1.5">Photos</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-[11px] text-gray-500 mb-1">User Photo</p>
                      {c.profilePic ? (
                        <img
                          src={c.profilePic}
                          alt="user"
                          onClick={() => setLightboxImg(c.profilePic)}
                          className="w-full h-28 object-cover rounded-lg border border-gray-200 cursor-zoom-in"
                        />
                      ) : (
                        <div className="h-28 rounded-lg border border-dashed border-gray-300 flex items-center justify-center text-gray-400 text-xs">
                          Not uploaded
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="text-[11px] text-gray-500 mb-1">Aadhaar Card</p>
                      {aadhar ? (
                        <img
                          src={aadhar}
                          alt="aadhaar"
                          onClick={() => setLightboxImg(aadhar)}
                          className="w-full h-28 object-cover rounded-lg border border-gray-200 cursor-zoom-in"
                        />
                      ) : (
                        <div className="h-28 rounded-lg border border-dashed border-gray-300 flex items-center justify-center text-gray-400 text-xs">
                          Not uploaded
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Sales */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-xs font-bold text-gray-700">
                      Sales ({sales.length})
                    </p>
                    <p className="text-sm font-extrabold text-emerald-700">
                      {kg.toLocaleString()} KG
                    </p>
                  </div>
                  {sales.length === 0 ? (
                    <div className="text-sm text-gray-400 text-center py-4 bg-gray-50 rounded-xl">
                      Koi sale entry nahi.
                    </div>
                  ) : (
                    <div className="rounded-xl border border-gray-200 overflow-hidden">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-50 text-gray-600">
                          <tr className="text-left">
                            <th className="px-3 py-2 font-bold">Date</th>
                            <th className="px-3 py-2 font-bold text-right">Qty (KG)</th>
                            <th className="px-3 py-2 font-bold">Remarks</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {sales.map((s) => (
                            <tr key={s._id}>
                              <td className="px-3 py-2 whitespace-nowrap">{fmtDate(s.date)}</td>
                              <td className="px-3 py-2 text-right font-bold text-gray-800 whitespace-nowrap">
                                {Number(s.quantity).toLocaleString()}
                              </td>
                              <td className="px-3 py-2 text-gray-600">{s.remarks || "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-1">
                  <button
                    onClick={() => {
                      setDetailCust(null);
                      openSale(String(c._id));
                    }}
                    className="flex-1 flex items-center justify-center gap-1.5 bg-yellow-500 hover:bg-yellow-600 text-white font-bold text-sm py-2.5 rounded-xl transition"
                  >
                    <AddchartIcon sx={{ fontSize: 16 }} /> Add Sale
                  </button>
                  <button
                    onClick={() => {
                      setDetailCust(null);
                      openEditCustomer(c);
                    }}
                    className="flex-1 flex items-center justify-center gap-1.5 bg-gray-800 hover:bg-gray-900 text-white font-bold text-sm py-2.5 rounded-xl transition"
                  >
                    <EditOutlinedIcon sx={{ fontSize: 16 }} /> Edit
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Full-screen image preview */}
      {lightboxImg && (
        <div
          className="fixed inset-0 bg-black/90 z-[10000] flex items-center justify-center p-4"
          onClick={() => setLightboxImg("")}
        >
          <button
            onClick={() => setLightboxImg("")}
            className="absolute top-4 right-4 text-white bg-white/20 hover:bg-white/30 rounded-full w-10 h-10 flex items-center justify-center"
          >
            <CloseIcon />
          </button>
          <img
            src={lightboxImg}
            alt="preview"
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
          />
        </div>
      )}

      <ToastContainer position="top-center" autoClose={3000} hideProgressBar theme="colored" />
    </div>
  );
}
